import type { PriceUnitDto } from '@/api';
import {
  formatPriceField,
  formatRm,
  formatSimulationCalculatedIndex,
} from '@/components/table/table-format-helpers';
import {
  hasPriceChange,
  matchesColumnFilter,
  NUMERIC_FILTER_COLUMNS,
} from '@/components/table/table-helpers';
import type { NumericFilterValue } from '@/components/table/table-types';
import type { MRT_ColumnFiltersState } from 'material-react-table';
import { formatIndexRowForDisplay } from '../components/simulation-index-section/simulation-index-helpers';
import {
  calculateLocalLeadNruIndex,
  calculateLpCoIncrease,
  computeSimulationIndexes,
  formatDataForDisplay,
  groupDataByPriceList,
  recalculateFields,
} from '../utils/simulation-helpers';
import type { SimulationIndexRow } from '../utils/simulation-index-types';
import type {
  PricelistSettings,
  RowData,
  RowDataForDisplay,
} from '../utils/simulation-types';

export type KeyFigureField =
  | 'rrpIndex'
  | 'rm'
  | 'lpLocalLeadIndex'
  | 'nipLocalLeadIndex'
  | 'nipGlobalLeadIndex'
  | 'nruLocalLeadIndex'
  | 'salesRevenue'
  | 'listPriceFmcFactor'
  | 'lpCoIncrease';

type KeyfiguresRow = Partial<Record<KeyFigureField, string>>;

export type KeyfiguresColumn = {
  header: string;
  row?: KeyfiguresRow;
  isHighlighted?: boolean;
};

export type KeyfiguresIndexRow = SimulationIndexRow & {
  nruLocalLeadIndex: number;
  lpCoIncrease: number;
};

export const computeKeyfigures = (
  rows: RowData[],
  pricelistSettings: Map<string, PricelistSettings>,
  priceUnits: PriceUnitDto[],
): KeyfiguresIndexRow[] => {
  const groupedRows = groupDataByPriceList(rows);
  return computeSimulationIndexes(rows, pricelistSettings, priceUnits).map(
    (indexRow) => {
      const priceListId = indexRow.priceList.id.toString();
      const priceListRows = groupedRows[priceListId] ?? [];
      const settings = pricelistSettings.get(priceListId);
      return {
        ...indexRow,
        nruLocalLeadIndex: settings
          ? calculateLocalLeadNruIndex(priceListRows, settings)
          : 0,
        lpCoIncrease: calculateLpCoIncrease(priceListRows),
      };
    },
  );
};

const formatRows = (
  rows: RowData[],
  pricelistSettings: Map<string, PricelistSettings>,
  totalLlRrpIndex: Map<string, number>,
): RowDataForDisplay[] => {
  const rowsByKey = new Map(rows.map((row) => [row.primaryKey, row]));
  return formatDataForDisplay(
    Array.from(recalculateFields(rowsByKey, pricelistSettings).values()),
    pricelistSettings,
    totalLlRrpIndex,
  );
};

// Formats the rows in the same way as the simulation table, so the table filters can be applied to them.
export const formatSimulationRows = ({
  rows,
  asIsRows,
  pricelistSettings,
  totalLlRrpIndex,
}: {
  rows: RowData[];
  asIsRows: RowData[];
  pricelistSettings: Map<string, PricelistSettings>;
  totalLlRrpIndex: Map<string, number>;
}): RowDataForDisplay[] => {
  const asIsRowsByKey = new Map(
    formatRows(asIsRows, pricelistSettings, totalLlRrpIndex).map((row) => [
      row.primaryKey,
      row,
    ]),
  );
  return formatRows(rows, pricelistSettings, totalLlRrpIndex).map((row) => ({
    ...row,
    hasPriceChange: hasPriceChange(row, asIsRowsByKey) ? '1' : '0',
  }));
};

// Returns the rows that pass the table filters. The price list filter is ignored.
export const filterSimulationRows = ({
  rows,
  formattedRows,
  columnFilters,
}: {
  rows: RowData[];
  formattedRows: RowDataForDisplay[];
  columnFilters: MRT_ColumnFiltersState;
}): RowData[] => {
  const filters = columnFilters.filter(({ id }) => id !== 'priceUnitId');
  if (filters.length === 0) return rows;

  const passedKeys = new Set(
    formattedRows
      .filter(
        (row) =>
          row.isExemptFromFiltering ||
          filters.every(({ id, value }) =>
            matchesColumnFilter(
              String(row[id as keyof RowDataForDisplay] ?? ''),
              id,
              value as NumericFilterValue | string[] | undefined,
              NUMERIC_FILTER_COLUMNS,
            ),
          ),
      )
      .map((row) => row.primaryKey),
  );
  return rows.filter((row) => passedKeys.has(row.primaryKey));
};

const formatKeyfiguresRow = (
  row: KeyfiguresIndexRow | undefined,
): KeyfiguresRow | undefined =>
  row && {
    ...formatIndexRowForDisplay(row),
    nruLocalLeadIndex: formatSimulationCalculatedIndex(row.nruLocalLeadIndex),
    salesRevenue: formatPriceField(row.salesRevenue, true, 1),
    lpCoIncrease: formatRm(row.lpCoIncrease),
  };

const subtractIndexRows = (
  row: KeyfiguresIndexRow,
  baseRow: KeyfiguresIndexRow,
): KeyfiguresIndexRow => ({
  ...row,
  rrpIndex: row.rrpIndex - baseRow.rrpIndex,
  rrpLocalLeadExclVatIndex:
    row.rrpLocalLeadExclVatIndex - baseRow.rrpLocalLeadExclVatIndex,
  rm: row.rm - baseRow.rm,
  lpLocalLeadIndex: row.lpLocalLeadIndex - baseRow.lpLocalLeadIndex,
  nipLocalLeadIndex: row.nipLocalLeadIndex - baseRow.nipLocalLeadIndex,
  nipGlobalLeadIndex: row.nipGlobalLeadIndex - baseRow.nipGlobalLeadIndex,
  listPriceFmcFactorTarget:
    row.listPriceFmcFactorTarget - baseRow.listPriceFmcFactorTarget,
  listPriceFmcFactor: row.listPriceFmcFactor - baseRow.listPriceFmcFactor,
  listPriceFmcFactorDifference:
    row.listPriceFmcFactorDifference - baseRow.listPriceFmcFactorDifference,
  salesRevenue: row.salesRevenue - baseRow.salesRevenue,
  productCount: row.productCount - baseRow.productCount,
  nruLocalLeadIndex: row.nruLocalLeadIndex - baseRow.nruLocalLeadIndex,
  lpCoIncrease: row.lpCoIncrease - baseRow.lpCoIncrease,
});

export const buildKeyfiguresColumns = ({
  planningYear,
  row,
  asIsRows,
  previousYearRows,
  filteredRows,
  filteredAsIsRows,
}: {
  planningYear: number;
  row: KeyfiguresIndexRow;
  asIsRows: KeyfiguresIndexRow[];
  previousYearRows: KeyfiguresIndexRow[];
  filteredRows: KeyfiguresIndexRow[];
  filteredAsIsRows: KeyfiguresIndexRow[];
}): { grandTotal: KeyfiguresColumn[]; filteredValues: KeyfiguresColumn[] } => {
  const priceListId = row.priceList.id;
  const asIsRow = asIsRows.find((asIs) => asIs.priceList.id === priceListId);
  const filteredRow = filteredRows.find(
    (filtered) => filtered.priceList.id === priceListId,
  );
  const filteredAsIsRow = filteredAsIsRows.find(
    (filteredAsIs) => filteredAsIs.priceList.id === priceListId,
  );

  const previousYear: KeyfiguresColumn = {
    header: `${planningYear - 1}`,
    row: formatKeyfiguresRow(
      previousYearRows.find(
        (previousRow) => previousRow.priceList.id === priceListId,
      ),
    ),
  };
  const currentYear: KeyfiguresColumn = {
    header: `${planningYear}`,
    row: formatKeyfiguresRow(asIsRow),
  };
  const simulation: KeyfiguresColumn = {
    header: `${planningYear} Sim`,
    row: formatKeyfiguresRow(row),
  };
  const deviation: KeyfiguresColumn = {
    header: 'Dev*',
    isHighlighted: true,
    row: formatKeyfiguresRow(asIsRow && subtractIndexRows(row, asIsRow)),
  };
  const filteredCurrentYear: KeyfiguresColumn = {
    header: `${planningYear}`,
    row: formatKeyfiguresRow(filteredAsIsRow),
  };
  const filteredSimulation: KeyfiguresColumn = {
    header: `${planningYear} Sim`,
    row: formatKeyfiguresRow(filteredRow),
  };
  const filteredDeviation: KeyfiguresColumn = {
    header: 'Dev*',
    isHighlighted: true,
    row: formatKeyfiguresRow(
      filteredRow &&
        filteredAsIsRow &&
        subtractIndexRows(filteredRow, filteredAsIsRow),
    ),
  };

  return {
    grandTotal: [previousYear, currentYear, simulation, deviation],
    filteredValues: [
      filteredCurrentYear,
      filteredSimulation,
      filteredDeviation,
    ],
  };
};
